/**
 * CameraCaptura — overlay da câmera DENTRO da página, usado pelo botão
 * "Tirar foto" das solicitações.
 *
 * Por que existe (bug de produção 2026-07-30): o caminho antigo era um
 * `<input type="file" capture>`, que delega a captura ao sistema. No Chromium
 * para Android, quando o app Chrome está sem a permissão `android.permission
 * .CAMERA`, esse caminho ABORTA EM SILÊNCIO — `SelectFileDialog.java` faz
 * `if (mCapture) { onFileNotSelected(); return; }` sem toast, sem picker e sem
 * disparar `change`. O botão virava um no-op. Aparelhos Xiaomi/MIUI caem nisso
 * com frequência porque a MIUI revoga sozinha permissões de apps pouco usados.
 *
 * O getUserMedia não escapa da permissão do sistema (nada escapa), mas faz o
 * Chrome EXIBIR o pedido quando ele é recuperável e falha com erro CAPTURÁVEL,
 * o que permite ao app explicar e oferecer a galeria em vez de morrer calado.
 *
 * ESTE COMPONENTE É APENAS APRESENTAÇÃO. Quem adquire e encerra o MediaStream
 * é o FotosInput (dono único) — sem isso o stream vaza em várias sequências
 * (desmontar durante a aquisição, StrictMode, troca de câmera) e a câmera do
 * aparelho fica presa com o LED aceso.
 *
 * Props:
 * - `stream` — MediaStream vivo, fornecido pelo dono;
 * - `podeTrocar` — mostra o botão de virar a câmera;
 * - `onTrocarCamera()`, `onCapturar(video)`, `onFechar()`.
 */
import React, { useEffect, useRef, useState } from "react";
import { FiCamera, FiRefreshCw, FiX } from "react-icons/fi";

import {
  CamAcoes, CamCapturarBtn, CamFecharBtn, CamOverlay, CamPalco, CamTrocarBtn,
  CamVideo,
} from "./styles";

/** true quando o navegador expõe a API (exige HTTPS ou localhost). */
export function cameraEmbutidaDisponivel() {
  return Boolean(
    typeof navigator !== "undefined" && navigator.mediaDevices?.getUserMedia,
  );
}

/**
 * Permissão negada (pelo site ou pelo sistema) — único caso em que insistir na
 * câmera é inútil: o caminho nativo `capture` bateria na MESMA permissão.
 */
export function ehPermissaoNegada(erro) {
  return erro?.name === "NotAllowedError" || erro?.name === "SecurityError";
}

export function mensagemDeErroCamera(erro) {
  switch (erro?.name) {
    case "NotAllowedError":
    case "SecurityError":
      return "A câmera está bloqueada para este navegador. Tire a foto pelo app de Câmera do celular e escolha aqui pela galeria.";
    case "NotFoundError":
    case "OverconstrainedError":
      return "Nenhuma câmera foi encontrada neste aparelho.";
    case "NotReadableError":
      return "A câmera está ocupada por outro aplicativo. Feche o app de câmera e tente de novo.";
    default:
      return "Não foi possível abrir a câmera.";
  }
}

/**
 * Só está apto a capturar com imagem VIVA: track live e sem `muted` (o Android
 * silencia a track em segundo plano) e vídeo tocando com frame disponível.
 * Sem checar `paused`/`muted`, voltar do segundo plano com a prévia congelada
 * gravaria o frame velho como se fosse a foto.
 */
function apto(video, stream) {
  const track = stream?.getVideoTracks?.()[0];
  return Boolean(
    video && video.readyState >= 2 && video.videoWidth > 0 && !video.paused
    && track && track.readyState === "live" && !track.muted,
  );
}

export default function CameraCaptura({
  stream,
  podeTrocar,
  trocando,
  onTrocarCamera,
  onCapturar,
  onFechar,
}) {
  const videoRef = useRef(null);
  const [pronta, setPronta] = useState(false);

  // Anexa o stream do dono ao <video> e mantém `pronta` colada na realidade:
  // qualquer evento que possa congelar a imagem recalcula o estado, e o botão
  // do obturador desabilita junto (nunca fica clicável e inerte).
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !stream) return undefined;
    const track = stream.getVideoTracks?.()[0];
    const recalcular = () => setPronta(apto(video, stream));

    setPronta(false);
    video.srcObject = stream;
    video.play().then(recalcular).catch(() => {
      /* autoplay bloqueado: os eventos abaixo ainda liberam o botão */
    });

    const aoVoltarAoFrente = () => {
      if (document.visibilityState !== "visible") {
        recalcular();
        return;
      }
      video.play().then(recalcular).catch(recalcular);
    };

    const eventosVideo = ["canplay", "playing", "pause", "waiting", "emptied"];
    eventosVideo.forEach((ev) => video.addEventListener(ev, recalcular));
    ["mute", "unmute", "ended"].forEach((ev) =>
      track?.addEventListener(ev, recalcular));
    document.addEventListener("visibilitychange", aoVoltarAoFrente);

    return () => {
      eventosVideo.forEach((ev) => video.removeEventListener(ev, recalcular));
      ["mute", "unmute", "ended"].forEach((ev) =>
        track?.removeEventListener(ev, recalcular));
      document.removeEventListener("visibilitychange", aoVoltarAoFrente);
    };
  }, [stream]);

  // Voltar do Android / Esc fecham o OVERLAY, não a página. Sem isso o gesto
  // de voltar sairia do wizard e levaria junto o formulário preenchido.
  useEffect(() => {
    window.history.pushState({ cameraPharuErp: true }, "");
    // Só fecha quando o popstate SAIU das nossas entradas. Sem essa checagem,
    // o duplo-invoke de efeitos do StrictMode (dev) empurra/volta uma entrada
    // e o overlay se fecharia sozinho no instante em que abre.
    const aoVoltar = (e) => {
      if (e.state?.cameraPharuErp) return;
      onFechar();
    };
    const aoTeclar = (e) => {
      if (e.key === "Escape") fecharViaHistorico();
    };
    window.addEventListener("popstate", aoVoltar);
    window.addEventListener("keydown", aoTeclar);
    return () => {
      window.removeEventListener("popstate", aoVoltar);
      window.removeEventListener("keydown", aoTeclar);
      // Fechou por outro caminho (capturou a foto): consome a entrada que
      // empurramos, senão o próximo "voltar" do usuário seria engolido.
      if (window.history.state?.cameraPharuErp) window.history.back();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fecha consumindo a entrada que empurramos no histórico, para não deixar
  // um "voltar" órfão que o usuário teria de tocar duas vezes.
  const fecharViaHistorico = () => {
    if (window.history.state?.cameraPharuErp) window.history.back();
    else onFechar();
  };

  return (
    <CamOverlay role="dialog" aria-modal="true" aria-label="Tirar foto">
      <CamFecharBtn
        type="button"
        onClick={fecharViaHistorico}
        aria-label="Fechar câmera"
      >
        <FiX size={22} />
      </CamFecharBtn>

      <CamPalco>
        <CamVideo ref={videoRef} autoPlay muted playsInline />
      </CamPalco>

      <CamAcoes>
        {podeTrocar ? (
          <CamTrocarBtn
            type="button"
            onClick={onTrocarCamera}
            disabled={trocando}
            aria-label="Trocar de câmera"
          >
            <FiRefreshCw size={18} />
          </CamTrocarBtn>
        ) : (
          <span style={{ width: 48 }} aria-hidden="true" />
        )}
        <CamCapturarBtn
          type="button"
          onClick={() => {
            if (apto(videoRef.current, stream)) onCapturar(videoRef.current);
          }}
          disabled={!pronta || trocando}
          aria-label="Capturar foto"
        >
          <FiCamera size={26} />
        </CamCapturarBtn>
        <span style={{ width: 48 }} aria-hidden="true" />
      </CamAcoes>
    </CamOverlay>
  );
}
